/* eslint-disable @typescript-eslint/naming-convention */
import api from '@/api';
import {
  API_ENDPOINTS,
  CUSTOMER_ACCESS_TOKEN_KEY,
  DEFAULT_RZP_KEY,
  DEFAULT_RZP_STAGE_KEY,
  ENV_MAP,
} from '@/constants';
import { ApiFunctionResponse, SendEmailOtpResponse, VerifyEmailOtpResponse } from '@/api/types';
import { IUser } from '@/@types/globals';

export interface VerifyOTPResponse {
  error: any;
  message: string;
}

/* eslint-disable @typescript-eslint/naming-convention */
declare const __STAGE__: string;

const getMerchantKey = (key: string): string => {
  if (key) return key;
  const isProd = __STAGE__ === ENV_MAP.PRODUCTION;
  return isProd ? DEFAULT_RZP_KEY : DEFAULT_RZP_STAGE_KEY;
};

/**
 * Sends an OTP to the user's phone number
 *
 * @param contact The user's phone number
 */
export const sendOtp = async (
  contact: string,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  merchant_key: string,
): Promise<ApiFunctionResponse> => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const response = await api(
    API_ENDPOINTS.OTP_CREATE,
    { key_id: getMerchantKey(merchant_key) },
    {
      method: 'POST',
      body: new URLSearchParams({
        contact,
        otp_reason: 'instant_emi_login',
      }),
    },
    'application/x-www-form-urlencoded',
  );

  if (response.ok) {
    return { success: true };
  } else {
    return { success: false };
  }
};

/**
 *
 * Verifies the OTP entered by the user
 *
 * @param contact The user's phone number
 * @param otp The OTP entered by the user
 */
export const verifyOtp = async (
  contact: string,
  otp: string,
  key: string,
): Promise<ApiFunctionResponse> => {
  const response = await api(
    API_ENDPOINTS.OTP_VERIFY,
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      key_id: getMerchantKey(key),
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    {
      method: 'POST',
      body: JSON.stringify({ contact, otp, key_id: getMerchantKey(key) }),
      credentials: 'include',
    },
  );

  if (response.ok) {
    const xCustomerToken = response.headers.get(CUSTOMER_ACCESS_TOKEN_KEY);
    return {
      success: true,
      headers: {
        xCustomerToken,
      },
    };
  } else {
    const data = (await response.json()) as VerifyOTPResponse;
    return { success: false, payload: data.error?.description };
  }
};

export const fetchCustomerAuthToken = async (token: string): Promise<any> => {
  const response = await api(
    API_ENDPOINTS.CUSTOMER_REFRESH_ROUTE,
    {
      [CUSTOMER_ACCESS_TOKEN_KEY]: token,
    },
    {
      method: 'POST',
      credentials: 'include',
    },
  );

  if (response.ok) {
    const xCustomerToken = response.headers.get(CUSTOMER_ACCESS_TOKEN_KEY);
    return {
      success: true,
      headers: {
        xCustomerToken,
      },
    };
  } else {
    const data = (await response.json()) as VerifyOTPResponse;
    return { success: false, payload: data.message };
  }
};

export const sendEmailOtp = async ({
  body,
  headers,
}: {
  body: IUser;
  headers: Record<string, string>;
}): Promise<SendEmailOtpResponse> => {
  const { email } = body;
  const response = await api(
    API_ENDPOINTS.EMAIL_OTP_SEND,
    {},
    { method: 'POST', headers, body: JSON.stringify({ email }) },
  );

  if (response.ok) {
    return { success: true };
  } else {
    const data = (await response.json()) as ApiFunctionResponse;
    return {
      success: false,
      errors: data.payload?.details?.map(({ code, description }) => ({ code, description })),
    };
  }
};

export const verifyEmailOtp = async ({
  body,
  headers,
}: {
  body: {
    email: string;
    otp: string;
  };
  headers: Record<string, string>;
}): Promise<VerifyEmailOtpResponse> => {
  const { email, otp } = body;
  const response = await api(
    API_ENDPOINTS.EMAIL_OTP_VERIFY,
    {},
    { method: 'POST', headers, body: JSON.stringify({ email, otp }) },
  );

  if (response.ok) {
    return { success: true };
  } else {
    const data = (await response.json()) as ApiFunctionResponse;
    return {
      success: false,
      errors: data.payload?.details?.map(({ code, description }) => ({ code, description })),
    };
  }
};
